import { useState, useEffect } from "react";
import styled from "styled-components";
import { Skeleton, Button } from "@mui/material";
import PrepareFetch from "../helpers/PrepareFetch";
import alertSkeleton from "../assets/icons/alertSkeleton.svg";
import StyledButton from "../atom/Button";
import PointsFormatUtil from "../helpers/PointFormatUtil";
import LuckyNumberFormatUtil, { PromoEndDateFormat } from "../helpers/LuckyNumberFormatUtil";
import Breakpoints from "../config/Breakpoints";
import LuckyNumberPagination from "../atom/LuckyNumberPagination";
import MuiBlock from "../atom/MuiCustoms";
import { LuckyNumberDownload, LuckyNumberCopyToClipboard } from "../atom/LuckyNumberDownload";

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1.5rem;

     .warningBlock {
        display: flex;
        flex-direction: column;
        gap: 1rem;
        width: 100%;
        height: auto;
        border-radius: .5rem;
        background: var(--color-xLightGray);
        color: var(--color-gray);
        justify-content: center;
        align-items: center;
        padding: 2rem;
        box-sizing: border-box;

        &.onlyWarn {
            background: none;
            border: 1px solid var(--color-lightGray);
            color: var(--color-darkGray);
            min-height: 17.685rem;
        }
    }

    .cardAnatomy {
        border-radius: .5rem;
        min-height: 3.75rem;
        box-sizing: border-box;
    }
`;

const Greeting = styled.div`
    display: flex;
    font-size: var(--font-size-xl);
    padding: .5rem 1rem;
    background: var(--color-blueGrayLight);
    align-items: center;

    @media ${Breakpoints.max.xs} {
        padding: 1rem;
    }
    
    .text {
        flex: 1;
        
        @media ${Breakpoints.max.xs} {
            text-align: center;
        }
    }

    .buttons {
        display: flex;

        @media ${Breakpoints.max.xs} {
            display: none;
        }
    }

`;

const NumbersContainer = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 1rem;
    min-height: 17.685rem;

    @media ${Breakpoints.max.xs} {
        grid-template-columns: 1fr 1fr;
    }
`

const NumberContainer = styled.div`
    display: flex;
    padding: 1rem;
    align-items: center;
    justify-content: center;
    border: 1px solid var(--color-lightGray);
    font-weight: var(--font-weight-bold);
    gap: .5rem;
    position: relative;

    & > strong {
        font-size: var(--font-size-lg);
        font-weight: var(--font-size-bold);
        flex: 1;
    }

    &.isWinner:after {
        content: 'Premiado';
        display: inline-flex;
        padding: .25rem;
        border-radius: .25rem;
        background: var(--color-brand);
        color: var(--color-white);
        text-transform: uppercase;
        font-size: var(--font-size-xs);
        font-weight: var(--font-weight-bold);
    }

    @media ${Breakpoints.max.xs} { 
        &.isWinner {
            padding-top: 1.25rem;
            padding-bottom: 0.75rem;

            &:after {
                position: absolute;
                left: 1rem;
                top: -.5rem;
            }
        } 
    }
    
`;

const ModalFooter = styled.div`
    display: flex;
    gap: 1rem;

    .changeTab {
        flex: 1;

        .button {
            
        }
    }
    
    @media ${Breakpoints.max.sm} {
        flex-direction: column;

        .changeTab {
            order: 2;
            text-align: center;
        }
        .pagination {
            order: 1;
            align-self: 
        }
    }
`;

const LuckyNumberList = ({promoData, changeMode, style}) => {
    const pageSize = 12;

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [data, setData] = useState(null);
    const [currentPageData, setCurrentPageData] = useState([]);
    const [totalPages, setTotalPages] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [strListNumbers, setStrListNumbers] = useState('');

    const loadLuckyNumbers = () => {
        const fetchData = PrepareFetch(`/proxy/services/apexrest/api/v1/luckynumber/promotions/${promoData.id}/luckynumbers?pageSize=0`,"GET",{
            "Content-Type": "application/json"
          });

        setLoading(true);

        try {
            fetch(fetchData.url, fetchData.options)
            .then(response => {
                if(response.status === 200) {
                    return response.json();
                } else {
                    throw new Error(response.message);
                }                
            })
            .then(json => {
                setLoading(false);
                let returnData = json;
                returnData.data.sort((a,b) => (a.isWinner === b.isWinner) ? 0 : (a.isWinner ? -1 : 1));
                setData(returnData);
                setStrListNumbers(returnData.data.map((item) => {
                    return `${item.luckyNumber}${item.isWinner ? ' (premiado)' : ''}\n`;
                }).join(''));
                setTotalPages(Math.ceil(json.total / pageSize));
            })
            .catch(error => {
                setLoading(false);
                setError(true);

                if(process.env.VERCEL_ENV !== "production") {
                    console.log(error);
                }
            });
        } catch (error) {
            setLoading(false);
            setError(true);
            
            if(process.env.VERCEL_ENV !== "production") {
                console.log(error);
            }
        } 
    }

    const getCurrentPageData = (page) => {
        if(data === null) return;
        let startIndex = (page - 1) * pageSize;
        let endIndex = startIndex + pageSize;
        setCurrentPageData(data.data.slice(startIndex, endIndex));
    }

    useEffect(() => {
        loadLuckyNumbers();
    },[]);

    useEffect(() => {
        getCurrentPageData(1);
    },[data])

    useEffect(() => {
        getCurrentPageData(currentPage);
    },[currentPage]);

    return (
        <Container style={{...style}}>
            {loading && (
                <>
                    <Skeleton variant='rectangular' className='cardAnatomy' />
                    <NumbersContainer>
                        <Skeleton variant='rectangular' className='cardAnatomy' />
                        <Skeleton variant='rectangular' className='cardAnatomy' />
                        <Skeleton variant='rectangular' className='cardAnatomy' />
                        <Skeleton variant='rectangular' className='cardAnatomy' />
                        <Skeleton variant='rectangular' className='cardAnatomy' />
                        <Skeleton variant='rectangular' className='cardAnatomy' />
                        <Skeleton variant='rectangular' className='cardAnatomy' />
                        <Skeleton variant='rectangular' className='cardAnatomy' />
                        <Skeleton variant='rectangular' className='cardAnatomy' />
                        <Skeleton variant='rectangular' className='cardAnatomy' />
                        <Skeleton variant='rectangular' className='cardAnatomy' />
                        <Skeleton variant='rectangular' className='cardAnatomy' />
                    </NumbersContainer>
                </>
            )}
            {!loading && error && (
                <div className="warningBlock">
                    <img src={alertSkeleton} alt="" />
                    Ocorreu um erro ao carregar o conteúdo.
                    <StyledButton onClick={loadLuckyNumbers} className="secondary small" style={{borderRadius: ".5rem"}}>
                        Tentar novamente
                    </StyledButton>
                </div>
            )}
            {!loading && !error && (
                <>
                    <Greeting className="cardAnatomy">
                        <div className='text'>
                            Olá, você&#160; 
                            {data.total > 0 && <>tem&#160;<strong>{PointsFormatUtil(data.total)} {data.total > 1 ? <>números</> : <>número</>} da sorte</strong></>}
                            {(!data.total || data.total === 0) && <><strong>não tem números da sorte</strong></>}
                        </div>
                        {data.total > 0 && (
                            <div className='buttons'>
                                <LuckyNumberDownload promoName={promoData.name} listOfNumbers={data.data} />
                                <LuckyNumberCopyToClipboard listOfNumbers={strListNumbers} />
                            </div>
                        )}
                    </Greeting>
                    {data.total > 0 && (
                        <NumbersContainer>
                            {currentPageData.map((item) => (
                                <NumberContainer key={`luckyNumber_${item.luckyNumber}`} className={`cardAnatomy ${item.isWinner ? 'isWinner' : ''}`} style={{height: 'fit-content'}}>
                                    <strong>{LuckyNumberFormatUtil(item.luckyNumber)}</strong>
                                </NumberContainer>
                            ))}
                        </NumbersContainer>
                    )}
                    {(!data.total || data.total === 0) && (
                        <div className="warningBlock" style={{textAlign: 'left', alignItems: 'flex-start'}}>
                            {(promoData.isEnded || !promoData.daysToParticipate) && <>Uma pena, você não conseguiu números para esta campanha</>}
                            {!promoData.isEnded && promoData.daysToParticipate && <span>Você tem até <strong>{PromoEndDateFormat(promoData.evaluateEndDate, promoData.daysToParticipate)}</strong> para participar</span>}
                        </div>
                    )}
                    <ModalFooter>
                        {data.total > 0 && changeMode && (
                            <MuiBlock className="changeTab">
                                <Button onClick={changeMode} className="button">Saiba como você ganhou estes números</Button>
                            </MuiBlock>
                        )}
                        <LuckyNumberPagination className="pagination" currentPage={currentPage} totalPages={totalPages} setCurrentPage={setCurrentPage} />
                    </ModalFooter>
                </>
            )}
            {loading && (
                <Skeleton variant='rectangular' className='cardAnatomy' style={{minHeight: '2.5rem'}} />
            )}
        </Container>
    );
}

export default LuckyNumberList;